// ---------------------------------------------------------------------------
// humanize-provenance — generationSource → creator-facing trust copy (F30).
//
// The generation-provenance worklog (use-channel-dispatch.ts) carries the one
// piece of the engine work-log surface that is meaningful to a creator: whether
// the rendered result REUSED a verified corpus example (high trust) or was
// freshly LLM-generated with no corpus backing (lower trust). Per the F15 /
// D-406 contract, developers keep the raw engineering copy
// ("retrieval — bound a verified corpus example" + the raw exampleId), while
// creators get this jargon-free phrasing and never see the internal exampleId.
//
// Kept as a small pure mapper (parallel to humanize-rule-id.ts) so the copy is
// centralized + unit-testable and never scattered as inline string literals.
// ---------------------------------------------------------------------------

/** The backend's generation-source discriminator (out.provenance.generationSource). */
export type GenerationSource = 'retrieval' | 'zero-shot'

/**
 * Creator-facing trust copy for a generation source. Honest, jargon-free, no
 * emoji (per feedback_no_emoji_icons.md), no internal exampleId.
 *
 * - retrieval → the result was bound from a verified, reusable design.
 * - anything else (zero-shot / older backend) → freshly generated, no reuse.
 */
export function humanizeGenerationSource(source: GenerationSource | string): string {
  return source === 'retrieval'
    ? 'Reused a verified design'
    : 'Created a fresh design'
}

/** One creator-facing trust line for a multi-block app-generation run. */
export interface HumanizedProvenanceSummary {
  label: string
  value: string
  tone: 'success' | 'warning' | 'neutral'
  /**
   * F3's rule applied here too: the producer names WHICH line and how many,
   * the copy module names the language. `label`/`value` stay as the English
   * fallback for any surface that renders a worklog without the locale table.
   */
  code: 'provenance-reused' | 'provenance-fresh'
  count: number
}

/**
 * Creator-facing summary of an app-generation provenance run (F30 — the
 * app-generation twin of the per-dispatch provenance humanization in
 * use-channel-dispatch.ts). Collapses the raw worklog vocabulary
 * ("zero-shot", "LLM-generated", "corpus example", "provenance", raw
 * exampleId) into jargon-free reuse/fresh counts. Honest, no emoji, never
 * surfaces the internal exampleId.
 *
 * - `reused` (retrieval) blocks → high trust ("Reused N verified designs").
 * - `fresh` (zero-shot + unreported) blocks → freshly generated, no reuse.
 *
 * Returns only the non-zero lines; an all-fresh run shows just the fresh
 * line, an all-reused run just the reused line.
 */
export function humanizeProvenanceSummary(
  reusedCount: number,
  freshCount: number,
): HumanizedProvenanceSummary[] {
  const out: HumanizedProvenanceSummary[] = []
  if (reusedCount > 0) {
    out.push({
      label: 'Reused',
      value: `${reusedCount} ${reusedCount === 1 ? 'block reuses a' : 'blocks reuse a'} verified design`,
      code: 'provenance-reused',
      count: reusedCount,
      tone: 'success',
    })
  }
  if (freshCount > 0) {
    out.push({
      label: 'Fresh',
      value: `${freshCount} ${freshCount === 1 ? 'block is a' : 'blocks are'} freshly created design${freshCount === 1 ? '' : 's'}`,
      code: 'provenance-fresh',
      count: freshCount,
      tone: 'warning',
    })
  }
  return out
}
